import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Grid, Card, CardContent, Paper, Stack,
  Divider, Chip, Tooltip, IconButton, Button
} from "@mui/material";
import { motion } from "framer-motion";
import dayjs from "dayjs";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import PaymentIcon from '@mui/icons-material/Payment';
import Swal from "sweetalert2";
import { fetchMyArrearsInvoice } from "../services/arrearsService";
import LoadingSpinner from "../components/LoadingSpinner";
import { useTranslation } from "react-i18next";

const brand = "#800b33";
const brandHover = "#6c0a2b";

const formatAmount = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return value ?? "0";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

export default function MemberCatchUpPayment() {
  const { t } = useTranslation();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadInvoice = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchMyArrearsInvoice();
      setInvoice(data); // This will be the invoice object or null
    } catch (err) {
      setError(err.message);
      Swal.fire({
        icon: "error",
        title: t("Failed to load data"),
        text: err.message,
        confirmButtonColor: brand,
      });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: (i = 1) => ({
      opacity: 1, y: 0,
      transition: { delay: i * 0.1, duration: 0.4 },
    }),
  };

 

  const handleCopy = (text) => {
      navigator.clipboard.writeText(text);
      Swal.fire({
        icon: 'success',
        title: t('Copied!'),
        toast: true,
        position: 'bottom-end',
        showConfirmButton: false,
        timer: 1500
      });
    };

  const getStatusColor = (status) => {
    switch (status) {
      case "PAID": return "success";
      case "PENDING": return "warning";
      case "EXPIRED": return "error";
      default: return "default";
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Typography color="error" align="center" sx={{ my: 4 }}>
        {t("Error: {{message}}", { message: error })}
      </Typography>
    );
  }

  // Professional handling of the "no invoice" case
  if (!invoice) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
          {t("All Caught Up!")}
        </Typography>
        <Typography color="text.secondary">
          {t("You have no pending arrears invoices at the moment.")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      
      {/* HEADER */}
      <Typography variant="h5" sx={{ fontWeight: 900, color: brand, mb: 1 }}>
        {t("Catch Up Payment (Arrears)")}
      </Typography>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 3 }}>
        {t("View and pay your outstanding contributions using your Bank Control Number.")}
      </Typography>

      <Grid container spacing={3}>
        
        {/* LEFT: Invoice Details */}
        <Grid item xs={12} md={5}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            
            {/* Payment Reference / Control Number */}
            <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, color: brand, mb: 1 }}>
                  {t("Bank Control Number")}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t("Use this number when paying at NMB.")}
                </Typography>

                <Paper sx={{
                  p: 2, display: "flex", justifyContent: "space-between",
                  alignItems: "center", background: "#f5f5f5"
                }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: "monospace", wordBreak: 'break-all' }}>
                    {invoice.payment_reference}
                  </Typography>

                  <Tooltip title={t("Copy")}>
                    <IconButton onClick={() => handleCopy(invoice.payment_reference)}>
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </Paper>
              </CardContent>
            </Card>

            {/* Invoice Summary */}
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, color: brand, mb: 2 }}>
                  {t("Invoice Summary")}
                </Typography>

                <Stack spacing={1.5}>
                  <Typography><b>{t("Invoice ID:")}</b> {invoice.invoice_id}</Typography>
                  <Typography><b>{t("Total Due:")}</b> {formatAmount(invoice.total_amount)} TZS</Typography>
                  <Typography><b>{t("Missed Periods:")}</b> {invoice.missed_periods}</Typography>
                  <Typography><b>{t("Base Amount:")}</b> {formatAmount(invoice.base_amount)} TZS</Typography>
                  <Typography><b>{t("SSC Amount:")}</b> {formatAmount(invoice.df_amount)} TZS</Typography>
                  

                  <Chip
                    label={t(invoice.status)}
                    color={getStatusColor(invoice.status)}
                    sx={{ width: "120px", fontWeight: "bold" }}
                  />
                </Stack>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* RIGHT: Metrics + Download + Log */}
        <Grid item xs={12} md={7}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            
            {/* Metrics Overview */}
            <Paper sx={{ p: 2, borderRadius: 3, boxShadow: 3, mb: 3 }}>
              <Grid container spacing={2} alignItems="center">

                <Grid item xs={6} sm={3} sx={{ textAlign: "center" }}>
                  <AccountBalanceWalletIcon sx={{ fontSize: 32, color: brand }} />
                  <Typography variant="body2" color="text.secondary">{t("Total Due")}</Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {formatAmount(invoice.total_amount)} TZS
                  </Typography>
                </Grid>

                <Grid item xs={6} sm={3} sx={{ textAlign: "center" }}>
                  <EventBusyIcon sx={{ fontSize: 32, color: brand }} />
                  <Typography variant="body2" color="text.secondary">{t("Missed")}</Typography>
                  <Typography variant="h6" fontWeight={700}>{invoice.missed_periods}</Typography>
                </Grid>

                <Grid item xs={6} sm={3} sx={{ textAlign: "center" }}>
                  <EventRepeatIcon sx={{ fontSize: 32, color: brand }} />
                  <Typography variant="body2" color="text.secondary">{t("SSC Amount")}</Typography>
                  <Typography variant="h6" fontWeight={700}>{formatAmount(invoice.df_amount)}</Typography>
                </Grid>

                <Grid item xs={6} sm={3} sx={{ textAlign: "center" }}>
                  <PaymentIcon sx={{ fontSize: 32, color: brand }} />
                  <Typography variant="body2" color="text.secondary">{t("Base Amount")}</Typography>
                  <Typography variant="h6" fontWeight={700}>{formatAmount(invoice.base_amount)}</Typography>
                </Grid>

              </Grid>
            </Paper>

            {/* Download Button */}
            {/* <Button
              variant="contained"
              fullWidth
              startIcon={<DownloadIcon />}
              sx={{
                py: 1.4,
                mb: 3,
                borderRadius: 2,
                bgcolor: brand,
                "&:hover": { bgcolor: brandHover },
              }}
            >
              Download PDF Invoice
            </Button> */}

            {/* Activity Log */}
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>

                <Typography variant="h6" sx={{ fontWeight: 700, color: brand, mb: 2 }}>
                  {t("Activity Log")}
                </Typography>

                <Paper sx={{ p: 2 }}>
                  <Typography variant="body2">
                    {t("Created: {{date}}", {
                      date: dayjs(invoice.created_at).format("DD MMM YYYY, hh:mm A"),
                    })}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {t("Updated: {{date}}", {
                      date: dayjs(invoice.updated_at).format("DD MMM YYYY, hh:mm A"),
                    })}
                  </Typography>
                </Paper>

              </CardContent>
            </Card>

          </motion.div>
        </Grid>

      </Grid>
    </Box>
  );
}
